/**
 * Agent-wallet ("1-click trading", #688) React context. Provider: AgentProvider.tsx. Opt-in: without a session (or
 * without the provider, e.g. in older test harnesses) everything signs with the wallet exactly as before.
 */
import { createContext, useContext } from 'react';
import type { Address } from 'viem';
import type { AgentSigner } from '../ticket/order';

export type AgentStatus = 'unavailable' | 'off' | 'enabling' | 'on';
/** Why 1-click trading cannot be used right now. */
export type AgentUnavailableReason = 'no-wallet' | 'ghost' | 'mainnet-off' | 'region';

export interface AgentContextValue {
  status: AgentStatus;
  unavailableReason: AgentUnavailableReason | null;
  /** Public part of the active session (never the key). */
  session: { agentAddress: Address; agentName: string; validUntil: number } | null;
  /** Signer for SubmitDeps.agent while status === 'on'. */
  signer: AgentSigner | null;
  /** Last error from enabling (wallet rejection, /exchange refusal...). */
  error: string | null;
  /** Info message, e.g. why 1-click trading was turned off. */
  notice: string | null;
  /** Generate a key, sign approveAgent in the wallet (one prompt), verify. Resolves true when on. */
  enable: () => Promise<boolean>;
  /** Forget the agent key locally. */
  disable: () => void;
  /** Forget the agent because Hyperliquid no longer accepts it; `message` is shown as the notice. */
  forget: (message: string) => void;
  clearMessages: () => void;
}

const noop = () => undefined;

export const AGENT_OFF: AgentContextValue = {
  status: 'unavailable',
  unavailableReason: 'no-wallet',
  session: null,
  signer: null,
  error: null,
  notice: null,
  enable: async () => false,
  disable: noop,
  forget: noop,
  clearMessages: noop,
};

export const AgentContext = createContext<AgentContextValue | null>(null);

/** The agent state, or a permanently-off value outside <AgentProvider> (wallet signing only). */
export function useAgent(): AgentContextValue {
  return useContext(AgentContext) ?? AGENT_OFF;
}
